import { isFunction, isNumber, isString, memoizeFunction } from '@agilepoint/utils-common';
import React, { KeyboardEvent, MouseEvent, FocusEvent, PureComponent, createRef } from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { selectThemeSemantics } from '../../styling';
import { excludeKeys, generateGridTemplate, insertCharAt, setNativeValue } from '../../utils';
import { FlexBox } from '../flex-box';
import { Icon } from '../icon';
import { typographyPresets } from '../typography';
import { IMaskedInputProps } from './masked-input-types';
import { MaskedInputState } from './theme';
import {
    getFixedContent, getPlaceholderInfo, getMaskSegmentInfo, validateMask,
    defaultTransformFunction, getNextFixedContent
} from './utils';

const MainIcon = styled(Icon) <{ $order?: number, $disabled?: boolean }>`
    transition: fill .15s ease-in-out;
    order: ${({ $order }) => $order};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const ClearIcon = styled(MainIcon).attrs({
    iconName: 'ap-close.outline',
    iconSize: '12'
})`
    cursor: pointer;
`;

const IconWrapper = styled(FlexBox) <{ $order: number }>`
    order: ${({ $order }) => $order};
`;

const getMaskedInputStyleByState = memoizeFunction((state: MaskedInputState) => {
    const getStyles = (theme: DefaultTheme) => {
        const { maskedInput: { colors } } = theme;
        return colors[state];
    };
    return css`
        background-color: ${({ theme }) => getStyles(theme).backgroundColor};
        border-color: ${({ theme }) => getStyles(theme).borderColor};
        color: ${({ theme }) => getStyles(theme).foregroundColor};

        ${MainIcon} {
            fill: ${({ theme }) => getStyles(theme).iconColor};
        };

        ${ClearIcon} {
            fill: ${({ theme }) => getStyles(theme).clearIconColor};
        }
    `;
});

const getMaskedInputStyle = memoizeFunction((state: MaskedInputState) => ({ disabled, error }: IMaskedInputProps) => getMaskedInputStyleByState(disabled ? 'disabled' : (error ? 'error' : state)));

const Wrapper = styled.div<{ $gap: string, $gridTemplate: string }>`
    display: inline-grid;
    align-items: center;
    grid-template: ${({ $gridTemplate }) => $gridTemplate};
    grid-gap: ${({ $gap }) => `${$gap}px`};
`;

const StyledInput = styled.input.attrs({
    className: String('ap-masked-input')
}) <{ $order: number }>`
    font-family: inherit;
    ${typographyPresets.textStyles.small};
    box-sizing: border-box;
    border: none;
    order: ${({ $order }) => $order};
    border-radius: ${selectThemeSemantics(({ borderRadius }) => borderRadius.curvedSmall)};
    background: inherit;
    color: inherit;
    cursor: inherit;
    height: 100%;
    width: 100%;
    :focus {
        outline: 0;
    };
`;

const RemoveCharActions: { [key: string]: string } = {
    Backspace: 'Backspace',
    Delete: 'Delete'
};

interface IMaskedInputState {
    value: string;
    isFocused?: boolean;
}

class MaskedInputBase extends PureComponent<IMaskedInputProps, IMaskedInputState> {

    //#region Variables Declaration

    static defaultProps: Partial<IMaskedInputProps> = {
        iconSize: '16',
        gap: '16',
        iconPosition: 'left',
        masks: []
    };

    private textInputRef = createRef<HTMLInputElement>();
    private cursorPosition = 0;

    //#endregion

    //#region Component Lifecycle Methods

    static getDerivedStateFromProps({ value, masks }: IMaskedInputProps) {
        if (isString(value)) {
            return {
                value: (value + getFixedContent(masks, value.length))
            };
        }
        return null;
    }

    constructor(props: IMaskedInputProps) {
        super(props);
        const { defaultValue, value, masks } = props;
        const initialValue = (value || defaultValue || '');
        this.state = {
            value: initialValue + getFixedContent(masks, initialValue.length)
        };
    }

    override componentDidUpdate() {
        this.setCursorPosition(this.cursorPosition);
    }

    override componentDidMount() {
        this.setCursorPosition();
    }

    override render() {
        const { className, elementRef, iconName, hidden, title, gap, iconPosition } = this.props;
        const hideClearIcon = this.shouldHideClearIcon();
        const gridTemplate = generateGridTemplate(!!iconName, !hideClearIcon, iconPosition !== 'left');

        return hidden ? null : (
            <Wrapper $gap={gap!} className={className} title={title} onClick={this.onWrapperClick}
                $gridTemplate={gridTemplate} ref={elementRef}>
                {this.renderMainIcon()}
                {this.renderTextbox()}
                {this.renderClearIcon()}
            </Wrapper>
        );
    }

    //#endregion

    //#region Sub-Component Renderers

    private renderMainIcon() {
        const { iconName, iconSize, disabled } = this.props;
        const refOrder = this.getRefOrder();
        return iconName ? (
            <MainIcon $order={refOrder} iconName={iconName} iconSize={iconSize} $disabled={disabled} />
        ) : null;
    }

    private renderTextbox() {
        const {
            label, ...rest
        } = this.props;
        const textboxProps = excludeKeys(rest, 'children', 'onValueChange', 'hidden', 'error', 'title', 'gap', 'elementRef', 'className', 'masks');
        const refOrder = this.getRefOrder();
        const displayValue = this.getDisplayValue();

        return (
            <StyledInput
                {...textboxProps}
                type="text"
                $order={Math.abs(refOrder - 1)}
                value={displayValue}
                ref={this.textInputRef}
                aria-label={label}
                onKeyDown={this.onKeyDown}
                onChange={this.onChange}
                onClick={this.onTextboxClick}
                onFocus={this.onTextboxFocus}
                onBlur={this.onBlur}
                onPaste={this.onPaste} />
        );
    }

    private renderClearIcon() {
        const { clearIconTitle } = this.props;
        const hideClearIcon = this.shouldHideClearIcon();
        const refOrder = this.getRefOrder();

        return hideClearIcon
            ? null
            : (
                <IconWrapper $order={Math.abs(refOrder - 2)} alignItems="center" title={clearIconTitle}>
                    <ClearIcon onClick={this.onClearIconClick} />
                </IconWrapper>
            );
    }

    //#endregion

    //#region Event Handlers

    private onTextboxClick = (ev: MouseEvent<HTMLInputElement>) => {
        const textbox = ev.target as HTMLInputElement;
        const { masks, onClick } = this.props;
        const selectionIndex = textbox.selectionStart ?? textbox.value.length;
        const fixedContent = getFixedContent(masks, selectionIndex);
        this.setCursorPosition(selectionIndex + fixedContent.length);
        if (isFunction(onClick)) {
            onClick(ev);
        }
    }

    private onTextboxFocus = (ev: FocusEvent<HTMLInputElement>) => {
        const { onFocus } = this.props;
        this.setCursorPosition();
        this.setState({
            isFocused: true
        });
        if (isFunction(onFocus)) {
            onFocus(ev);
        }
    }

    private onKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
        const { onKeyDown } = this.props;
        const { value } = this.state;
        const selectionIndex = (ev.target as HTMLInputElement).selectionStart ?? value.length;
        if (ev.key.length === 1 && !ev.ctrlKey && !ev.metaKey) {
            this.insertChar(ev.key, selectionIndex);
            ev.preventDefault();
        // eslint-disable-next-line no-prototype-builtins
        } else if (RemoveCharActions.hasOwnProperty(ev.key)) {
            this.removeChar(RemoveCharActions[ev.key], selectionIndex);
            ev.preventDefault();
        }
        if (isFunction(onKeyDown)) {
            onKeyDown(ev);
        }
    }

    private onClearIconClick = (e: React.MouseEvent) => {
        const { onClear } = this.props;
        if (isFunction(onClear)) {
            onClear(e);
        }
        this.setValue('');
    }

    private onBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
        const { onBlur } = this.props;
        const { value } = this.state;
        this.setState({
            isFocused: false
        });
        if (!this.validateAll(value)) {
            this.setValue('');
        }
        if (isFunction(onBlur)) {
            onBlur(ev);
        }
    }

    private onPaste = (ev: React.ClipboardEvent<HTMLInputElement>) => {
        const { onPaste } = this.props;
        const newValue = ev.clipboardData.getData('text');
        ev.preventDefault();
        if (!this.validateAll(newValue)) {
            return;
        }
        this.setValue(newValue);
        if (isFunction(onPaste)) {
            onPaste(ev);
        }
    }

    private onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { onValueChange, onChange } = this.props;
        const { target } = e;
        this.cursorPosition = (target.selectionStart || target.value.length);

        if (isFunction(onValueChange)) {
            onValueChange(target.value);
        }
        if (isFunction(onChange)) {
            onChange(e);
        }
    }

    private onWrapperClick = (ev: MouseEvent<HTMLInputElement>) => {
        const { onClick } = this.props;
        this.textInputRef.current?.focus();
        this.setCursorPosition();
        if (isFunction(onClick)) {
            onClick(ev);
        }
    }

    //#endregion

    //#region Validation Methods

    private validateMaskedValue(value: string, index: number) {
        const { masks } = this.props;
        if (masks.length === 0) {
            return true;
        }

        const maskSegmentInfo = getMaskSegmentInfo(masks, index);
        if (!maskSegmentInfo) {
            return false;
        }

        const { endIndex, startIndex } = maskSegmentInfo;
        const maskValue = value.substring(startIndex, endIndex);
        const { value: valueFromState } = this.state;
        return validateMask(maskValue, valueFromState, maskSegmentInfo);
    }

    private validateAll(value: string) {
        const { masks } = this.props;
        const { length: maskFieldLength } = getPlaceholderInfo(masks);
        if (maskFieldLength !== value.length) {
            return false;
        }
        let tempIdx = 0;
        return masks.every(({ fixedContent }) => {
            const maskLength = fixedContent ? fixedContent.length : 1;
            const result = this.validateMaskedValue(value, tempIdx);
            tempIdx += maskLength;
            return result;
        });
    }

    //#endregion

    //#region Value Modifiers

    private getTransformedText(value: string, index: number) {
        const { masks } = this.props;
        if (masks.length === 0) {
            return value;
        }

        const maskSegmentInfo = getMaskSegmentInfo(masks, index);
        if (!maskSegmentInfo) {
            return value;
        }

        const { endIndex, mask: maskInfo, startIndex } = maskSegmentInfo;
        const maskValue = value.substring(startIndex, endIndex);
        const { transform = defaultTransformFunction } = maskInfo;
        return value.substring(0, startIndex) + transform(maskValue) + value.substring(startIndex + 1);
    }

    private insertChar(key: string, selectionIndex: number) {
        const { masks } = this.props;
        const { value } = this.state;
        const newValue = selectionIndex < value.length ? insertCharAt(value, key, selectionIndex) : value + key;
        const index = selectionIndex < value.length ? selectionIndex : value.length;
        if (!this.validateMaskedValue(newValue, index)) {
            return;
        }
        const transformedText = this.getTransformedText(newValue, index);
        const nextFixedContent = getNextFixedContent(masks, index);
        const isCursorAtEnd = index === value.length;
        const transformedValue = transformedText + (isCursorAtEnd ? nextFixedContent : '');
        this.setValue(transformedValue);
        if (isCursorAtEnd) {
            return;
        }
        if (transformedValue === value) {
            this.setCursorPosition(index + nextFixedContent.length + 1);
        }
        this.cursorPosition = index + nextFixedContent.length + 1;
    }

    private removeChar(key: string, selectionIndex: number) {
        const { masks } = this.props;
        const { value } = this.state;
        let newValue = '';
        switch (key) {
            case RemoveCharActions['Backspace']:
                newValue = this.handleBackspace(selectionIndex);
                break;
            case RemoveCharActions['Delete']:
                newValue = this.handleDelete(selectionIndex);
                break;
            default:
                newValue = value;
                break;
        }
        const fixedContent = getFixedContent(masks, newValue.length);
        if (fixedContent.length !== selectionIndex) {
            this.setValue(newValue);
        }
    }

    private handleBackspace(selectionIndex: number) {
        const { masks } = this.props;
        const { value } = this.state;
        const prevContentFixed = getFixedContent(masks, selectionIndex - 1);
        return value.substring(0, selectionIndex - prevContentFixed.length - 1) + value.substring(selectionIndex);
    }

    private handleDelete(selectionIndex: number) {
        const { masks } = this.props;
        const { value } = this.state;
        const prevContentFixed = getFixedContent(masks, selectionIndex);
        return value.substring(0, selectionIndex) + value.substring(selectionIndex + prevContentFixed.length + 1);
    }

    private getDisplayValue() {
        const { masks, placeholder: defaultPlaceHolder } = this.props;
        const { value, isFocused } = this.state;
        const { placeholder, length } = getPlaceholderInfo(masks);
        if (isFocused || !defaultPlaceHolder) {
            return value + (placeholder ? placeholder.substring(value.length) : '');
        }
        return value.length === length ? value : '';
    }

    //#endregion

    //#region Other Methods

    private setValue(value: string) {
        const { masks } = this.props;
        this.setState({ value: value + getFixedContent(masks, value.length) });
        const { current: textbox } = this.textInputRef;
        if (!textbox) {
            return;
        }
        setNativeValue(textbox, value);
        textbox?.dispatchEvent(new Event('input', { bubbles: true }));
    }

    private setCursorPosition(position?: number | null) {
        const { value } = this.state;
        const { current: textbox } = this.textInputRef;
        const newPosition = isNumber(position)
            ? position
            : (
                (textbox && isNumber(textbox.selectionStart) && textbox.selectionStart > value.length)
                    ? value.length
                    : null
            );
        if (!textbox || !isNumber(newPosition)) {
            return;
        }
        textbox.setSelectionRange(newPosition, newPosition);
    }

    private shouldHideClearIcon() {
        const { allowToClear, disabled, masks } = this.props;
        const { value } = this.state;
        const firstMaskFixedContent = getFixedContent(masks, 0);
        return (!allowToClear || !value || value === firstMaskFixedContent || disabled);
    }

    private getRefOrder() {
        const { iconPosition } = this.props;
        return iconPosition === 'left' ? 0 : 2;
    }

    //#endregion
}

export const MaskedInput = styled(MaskedInputBase as React.ComponentType<IMaskedInputProps>).attrs({
    className: String('ap-masked-input-wrapper')
})`
    height: 40px;
    min-width: 50px;
    box-sizing: border-box;
    overflow: hidden;
    transition: all 0.15s ease-in-out;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
    border-radius: ${({ theme: { maskedInput: { composites } } }) => composites.borderRadius};
    border-style: ${({ theme: { maskedInput: { composites } } }) => composites.borderStyle};
    border-width: ${({ theme: { maskedInput: { composites } } }) => composites.borderWidth};
    padding: ${selectThemeSemantics(({ gap }) => `0 ${gap.xl}`)};
    ${getMaskedInputStyle('default')}
    &:hover {
        ${getMaskedInputStyle('hovered')};
    }
    &:focus-within, :active {
        ${getMaskedInputStyle('focussed')};
    };
`;

MaskedInput.displayName = 'MaskedInput';
